import { deepCompareEQ } from '@cf-shared/deep-helpers';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { IntakeFormState } from '@cf-shared/data-model/intake-form-state';

/**
 * df-b9d12c28a387: the answers that do NOT make the max-effort plan out of date.
 *
 * The intake form compares the snapshot taken when the max-effort plan was built
 * (`maxEffortPlanIntakeFormState`) with the live answers in two places. One decides whether
 * the user must press Recalculate before Save & Update is allowed. The other, in
 * somethingChanged(), throws the generated program away. They used to carry separate lists,
 * and only the first ignored meal frequency and sleep hours. So after a meal-frequency change
 * the user was allowed to rebuild, and the rebuild's own somethingChanged() call then threw the
 * new program away: the overview crashed and Finish saved no program at all. One list, used by
 * both, keeps them from disagreeing again.
 */
export const MAX_EFFORT_SNAPSHOT_IGNORE_MAP: Readonly<Record<string, true>> = Object.freeze({
  'maxEffortPlan': true,
  'maxEffortPlanIntakeFormState': true,
  'generatedFitnessPlan': true,
  'intakeDeltaPercent': true,
  'trainingVolumeWeeklyAvg': true,
  'nutritionCoaching': true,
  'nutritionTracking': true,
  // #3382: meal frequency only repartitions the daily total across meals — it does
  // NOT affect the workout/max-effort plan or the daily energy target, so it must
  // not force a full "Recalculate" (which would reset the deficit to the suggested
  // value and surface a daily total that diverges from the applied one). The
  // per-meal targets are re-solved & persisted via the keep-path repartition.
  'mealFrequency': true,
  'sleepHours': true,
  'time_utc': true,
  'mode': true,
  'intakeFormProgress': true,
  '_id': true,
  '_rev': true,
  '_conflicts': true,
  'db__trans': true,
});

/** the answers MAX_EFFORT_SNAPSHOT_IGNORE_MAP ignores although they are real answers the user gives */
const ANSWERS_THAT_KEEP_THE_PLAN = ['mealFrequency', 'sleepHours'];

/** which answers a snapshot comparison counts */
export enum SnapshotComparison {
  /** only the answers that make the max-effort plan out of date — the change-mode rule */
  PlanAffectingAnswers = 'PlanAffectingAnswers',
  /**
   * also meal frequency and sleep hours. New mode keeps this, its rule from before
   * df-b9d12c28a387: a first-time user who steps back and changes one of them is sent back
   * through Goals, and nothing in new mode asks the other question, so nothing disagrees.
   */
  AllAnswers = 'AllAnswers',
}

const ALL_ANSWERS_IGNORE_MAP: Readonly<Record<string, true>> = Object.freeze(Object.fromEntries(
  Object.keys(MAX_EFFORT_SNAPSHOT_IGNORE_MAP)
    .filter(key => !ANSWERS_THAT_KEEP_THE_PLAN.includes(key))
    .map(key => [key, true as const])));

/**
 * true when the live answers still match the ones the max-effort plan was built from.
 * `comparison` says which answers count; see SnapshotComparison.
 */
export function maxEffortSnapshotMatchesForm(
  snapshot: IntakeFormState,
  form: IntakeFormState,
  comparison: SnapshotComparison = SnapshotComparison.PlanAffectingAnswers,
): boolean {
  const ignoreMap = comparison === SnapshotComparison.AllAnswers ? ALL_ANSWERS_IGNORE_MAP : MAX_EFFORT_SNAPSHOT_IGNORE_MAP;
  return deepCompareEQ(snapshot, form, ignoreMap);
}

/** what the program overview refresh should do once its own await has returned */
export enum OverviewRedraw {
  /** the program it set out to draw is still the current one */
  Draw = 'Draw',
  /** a newer program was installed meanwhile; the refresh that installed it draws it */
  SkipReplaced = 'SkipReplaced',
  /** the program was cleared meanwhile; drawing it would throw, and the condition is reported */
  SkipCleared = 'SkipCleared',
}

/**
 * df-b9d12c28a387: the overview refresh checks the program, awaits a database load, and only
 * then builds the overview. Anything that runs during that await can replace or clear the
 * program, so it decides again afterwards rather than drawing whatever the field now holds.
 */
export function decideOverviewRedraw(checkedPlan: FitnessPlan, currentPlan: FitnessPlan | undefined): OverviewRedraw {
  if (!currentPlan) {
    return OverviewRedraw.SkipCleared;
  }
  if (currentPlan !== checkedPlan) {
    return OverviewRedraw.SkipReplaced;
  }
  return OverviewRedraw.Draw;
}
